import React, { useState } from 'react';
import { Users, Tag, AlertTriangle, Layers, Pencil, Save, Wand2 } from 'lucide-react';
import { Avatar } from '../Avatar';
import { UnavailableDataBadge } from '../UnavailableDataBadge';
import { generateCategory } from '../../services/chatGptApi';

interface Assignee {
  username: string;
  profilePicture?: string;
}

interface TaskMetadataProps {
  assignees: Assignee[];
  vertical: string | null;
  category: string | null;
  priority: number | null;
  isEnglish: boolean;
  summary: string | null;
  onUpdateCategory?: (newCategory: string) => void;
  onUpdatePriority?: (newPriority: number) => void;
}

const PRIORITY_OPTIONS = [
  { value: 1, label: { pt: 'Altíssima', en: 'Very High' }, color: 'bg-red-100 text-red-700 border-red-300' },
  { value: 2, label: { pt: 'Alta', en: 'High' }, color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 3, label: { pt: 'Média', en: 'Medium' }, color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { value: 4, label: { pt: 'Baixa', en: 'Low' }, color: 'bg-blue-100 text-blue-700 border-blue-300' }
];

const getPriorityColor = (priority: number | null): string => {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority);
  return option?.color || 'bg-gray-100 text-gray-700 border-gray-300';
};

const getPriorityLabel = (priority: number | null, isEnglish: boolean): string => {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority);
  return option ? (isEnglish ? option.label.en : option.label.pt) : (isEnglish ? 'Not defined' : 'Não definida');
};

const TaskMetadata: React.FC<TaskMetadataProps> = ({
  assignees,
  vertical,
  category,
  priority,
  isEnglish,
  summary,
  onUpdateCategory,
  onUpdatePriority,
}) => {
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editedCategory, setEditedCategory] = useState(category || '');
  const [isEditingPriority, setIsEditingPriority] = useState(false);
  const [isGeneratingCategory, setIsGeneratingCategory] = useState(false);

  const handleSaveCategory = () => {
    if (onUpdateCategory) {
      onUpdateCategory(editedCategory);
    }
    setIsEditingCategory(false);
  };

  const handlePriorityChange = (newPriority: number) => {
    if (onUpdatePriority) {
      onUpdatePriority(newPriority);
    }
    setIsEditingPriority(false);
  };

  const handleGenerateCategory = async () => {
    if (!summary || !onUpdateCategory) return;

    setIsGeneratingCategory(true);
    try {
      const generatedCategory = await generateCategory(vertical, summary);
      onUpdateCategory(generatedCategory);
    } catch (error) {
      console.error('Error generating category:', error);
    } finally {
      setIsGeneratingCategory(false);
    }
  };

  return (
    <div className="space-y-8 mb-8 print:space-y-4">
      {/* Assignees */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2 print:mb-2 print:text-sm">
          <Users className="w-5 h-5 text-blue-600 print:w-4 print:h-4" />
          {isEnglish ? 'Responsible' : 'Responsáveis'}
        </h3>
        <div className="space-y-2 print:space-y-1 print:flex print:flex-col print:items-start">
          {assignees?.length > 0 ? (
            assignees.map((assignee, index) => (
              <div key={index} className="print:w-full">
                <Avatar
                  src={assignee.profilePicture}
                  name={assignee.username}
                />
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic print:text-sm">
              {isEnglish ? 'No assigned responsible' : 'Nenhum responsável atribuído'}
            </p>
          )}
        </div>
      </div>

      {/* Metadata Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Vertical */}
        <div className="md:col-span-3">
          <h4 className="font-semibold mb-2 flex items-center gap-2 print:text-sm">
            <Layers className="w-4 h-4 text-blue-600" />
            {isEnglish ? 'Vertical' : 'Vertical'}
          </h4>
          <span className="px-3 py-1 rounded-full text-sm inline-block bg-blue-100 text-blue-700 border border-blue-200 print:text-xs print:px-2 print:py-0.5">
            {vertical || (isEnglish ? 'Not defined' : 'Não definida')}
          </span>
        </div>

        {/* Category - Expanded Space */}
        <div className="md:col-span-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center gap-2 print:text-sm">
              <Tag className="w-4 h-4 text-blue-600" />
              {isEnglish ? 'Category' : 'Categoria'}
            </h3>
            <div className="flex items-center gap-2">
              {summary && onUpdateCategory && !isEditingCategory && (
                <button
                  onClick={handleGenerateCategory}
                  disabled={isGeneratingCategory}
                  className="text-gray-600 hover:text-purple-800 print:hidden"
                  title={isEnglish ? 'Generate with AI' : 'Gerar com IA'}
                >
                  <Wand2 className={`w-4 h-4 ${isGeneratingCategory ? 'animate-spin' : ''}`} />
                </button>
              )}
              {onUpdateCategory && !isEditingCategory && (
                <button
                  onClick={() => setIsEditingCategory(true)}
                  className="text-gray-600 hover:text-gray-800 print:hidden"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {isEditingCategory ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={editedCategory}
                onChange={(e) => setEditedCategory(e.target.value)}
                className="flex-1 px-3 py-1 text-sm border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={isEnglish ? "Enter category..." : "Digite a categoria..."}
              />
              <button
                onClick={handleSaveCategory}
                className="p-1 text-purple-600 hover:text-purple-800"
              >
                <Save className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <span className="px-3 py-1 rounded-full text-sm inline-block bg-purple-100 text-purple-700 border border-purple-200 print:text-xs print:px-2 print:py-0.5">
              {category || (isEnglish ? 'Not defined' : 'Não definida')}
            </span>
          )}
        </div>

        {/* Priority */}
        <div className="md:col-span-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center gap-2 print:text-sm">
              <AlertTriangle className="w-4 h-4 text-blue-600" />
              {isEnglish ? 'Priority' : 'Prioridade'}
            </h3>
            {onUpdatePriority && !isEditingPriority && (
              <button
                onClick={() => setIsEditingPriority(true)}
                className="text-gray-600 hover:text-gray-800 print:hidden"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
          {isEditingPriority ? (
            <div className="space-y-2">
              {PRIORITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handlePriorityChange(option.value)}
                  className={`w-full px-3 py-1 rounded-md text-sm text-left border transition-colors ${option.color} hover:opacity-80`}
                >
                  {isEnglish ? option.label.en : option.label.pt}
                </button>
              ))}
            </div>
          ) : (
            <span 
              className={`px-3 py-1 rounded-full text-sm inline-block border print:text-xs print:px-2 print:py-0.5 ${getPriorityColor(priority)}`}
            >
              {getPriorityLabel(priority, isEnglish)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskMetadata;

export { TaskMetadata }